import { createAction } from '@reduxjs/toolkit';

const FETCHING_PARTNER_LIST = 'FETCHING_PARTNER_LIST';
const CREATE_PARTNER = 'CREATE_PARTNER';
const GET_PARTNER_DETAIL = 'GET_PARTNER_DETAIL';
const UPDATE_PARTNER = 'UPDATE_PARTNER';
const CLEAR_PARTNER = 'CLEAR_PARTNER';
const FETCHING_PARTNER_OPTIONS = 'FETCHING_PARTNER_OPTIONS';
const FETCHING_SYNC_REQUEST_LIST = 'FETCHING_SYNC_REQUEST_LIST';
const GET_SYNC_REQUEST_DETAIL = 'GET_SYNC_REQUEST_DETAIL';
const SYNC_DATA_FROM_PARTNER = 'SYNC_DATA_FROM_PARTNER';
const SYNC_DATA_PARTNER_BY_ID = 'SYNC_DATA_PARTNER_BY_ID';
const DELETE_SYNC_REQUESTS = 'DELETE_SYNC_REQUESTS';

export interface PartnerListItem {
    id?: number | string;
    name: string;
    code: string;
    status: 0 | 1;
    created_date?: string;
    updated_date?: string;
}

export type PartnerDetailType = Omit<PartnerListItem, 'id' | 'created_date' | 'updated_date'>;

export type UpdatePartnerPayload = {
    data: PartnerDetailType;
    id: string;
};

export interface SyncRequestItem {
    id: number;
    request_code: string;
    partner: number;
    request_type: string;
    created_date: string;
    date_sync: string | null;
    status: number;
    updated_date: string;
    school_name?: string | null;
    school_code?: string | null;
}

export interface SyncRequestSchoolDataRow {
    field: string;
    K12Online: string | number | null;
    Edux: string | number | null;
    status?: string;
}

export interface SyncRequestContent {
    edux_content_id: number | null;
    edux_module_name: string | null;
    partner_content_id: number | null;
    partner_module_name: string | null;
    edux_subject_code: string | null;
    embed_content_url: string | null;
    embed_report_url: string | null;
    status?: string | null;
}

export interface SyncRequestAccount {
    role: string;
    email: string;
    error: string | null;
    gender: string;
    status: string;
    username: string;
    full_name: string;
}

export interface SyncRequestClass {
    accounts: SyncRequestAccount[];
    contents: SyncRequestContent[];
    class_data: SyncRequestSchoolDataRow[];
}

export interface SyncRequestAccountError {
    message?: string;
    username?: string;
    [key: string]: any;
}

export interface SyncRequestDetail extends SyncRequestItem {
    school_data: SyncRequestSchoolDataRow[];
    contents: SyncRequestContent[];
    accounts: SyncRequestAccount[];
    account_errors: (string | SyncRequestAccountError)[];
    classes?: SyncRequestClass[];
}

export type SyncRequestListFilters = {
    request_type?: string | null;
    request_code?: string | null;
    status?: number | null;
    page?: number;
    page_size?: number;
    partner?: number | string;
};

export interface DeleteSyncRequestsResponse {
    success: boolean;
    deleted_count: number;
    missing_ids: number[];
}

export const fetchingPartnerList = createAction(FETCHING_PARTNER_LIST, (filters: any) => ({
    payload: filters,
}));

export const fetchingPartnerOptions = createAction(FETCHING_PARTNER_OPTIONS);

export const createPartner = createAction<PartnerDetailType>(CREATE_PARTNER);

export const getPartnerDetail = createAction(GET_PARTNER_DETAIL, (id: string) => ({
    payload: { id },
}));

export const clearPartner = createAction(CLEAR_PARTNER);

export const updatePartner = createAction(
    UPDATE_PARTNER,
    (data: PartnerDetailType, id: string) => ({
        payload: { data, id } satisfies UpdatePartnerPayload,
    }),
);

export interface SyncDataSummary {
    total_schools: number;
    total_requests: number;
    failed: number;
}

export interface SyncDataFailedItem {
    schoolCode: string;
    error: string;
}

export interface SyncDataFromPartnerResponse {
    message: string;
    summary: SyncDataSummary;
    failed_items: SyncDataFailedItem[];
}

export interface SyncByIdSummary {
    total: number;
    created: number;
    updated: number;
    failed: number;
}

export interface SyncByIdAccount {
    [key: string]: any;
}

export interface SyncByIdResult {
    summary: SyncByIdSummary;
    accounts: SyncByIdAccount[];
}

export interface SyncDataPartnerByIdResponse extends SyncRequestItem {
    sync_result: SyncByIdResult;
}

export const fetchingSyncRequestList = createAction(
    FETCHING_SYNC_REQUEST_LIST,
    (filters: SyncRequestListFilters) => ({ payload: filters }),
);

export const getSyncRequestDetail = createAction(
    GET_SYNC_REQUEST_DETAIL,
    (id: number) => ({ payload: { id } }),
);

export const syncDataFromPartner = createAction(
    SYNC_DATA_FROM_PARTNER,
    (params: { from_date: string; to_date: string; partnerId?: number | string; listFilters?: SyncRequestListFilters }) => ({ payload: params }),
);

export const syncDataPartnerById = createAction(
    SYNC_DATA_PARTNER_BY_ID,
    (id: number, partnerId?: number | string, listFilters?: SyncRequestListFilters) => ({
        payload: { id, partnerId, listFilters },
    }),
);

export const deleteSyncRequests = createAction(
    DELETE_SYNC_REQUESTS,
    (ids: number[], partnerId?: number | string, listFilters?: SyncRequestListFilters) => ({
        payload: { ids, partnerId, listFilters },
    }),
);
